/**
 * @license
 * Copyright (c) 2023 Entelechy Corporation. All rights reserved.
 */

import { PropertyValues, TemplateResult, css, html } from "lit"
import { customElement, property } from 'lit/decorators.js'
import { provide, createContext } from '@lit/context'
import { Task } from '@lit/task'
import { Route, Router } from '@vaadin/router'
import { ECComponent } from "./ec-component"
import { supabase } from "../Supabase"
import { AuthChangeEvent, Session, Subscription } from "@supabase/supabase-js"
import { ConfigType } from "../ConfigType"

declare const CONFIG: ConfigType

export const userContext = createContext<User>(Symbol('user'))
export const protContext = createContext<ProtectedModule|undefined>(Symbol('prot'))

@customElement('ec-app')
export class ECApp extends ECComponent {

  @provide({ context: userContext })
  @property({ attribute: false })
  user: User = {
    signedIn: false,
    session: null,
    avatarURL: ""
  }
  
  @provide({ context: protContext })
  @property({ attribute: false })
  prot: ProtectedModule | undefined = undefined

  private onAuthStateChangeSubscription: Subscription | undefined = undefined
  private catchAllRoute = { path: '(.*)', redirect: '/' }
  private routes: Route[] = [
    { path: '/', component: 'ec-home' },
    // { path: '/users', component: 'ec-users' },
    // { path: '/users/:username', component: 'ec-user' },
    { path: '/users/_/settings', component: 'ec-user' },
    { path: '/users/_/reset-pw', component: 'ec-reset-pw' },
    { path: '/users/_/signup', component: 'ec-signup' },
    { path: '/users/_/signin', component: 'ec-signin' },
    this.catchAllRoute
  ]

  static styles = [
    ...ECComponent.styles,
    css`
      .page {
        width: 100vw;
        height: calc(100vh - 10px);
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.33);
        display: grid;
        grid-template-rows: min-content minmax(10px, 4fr) min-content;
        gap: 1px;
      }
      .page > * {
        padding: 0.25rem;
      }
      @media (max-width: 600px) {
        .page {
          grid-template-columns: 100%;
          grid-template-rows: minmax(2em, fit-content);
        }
        .page > * {
          grid-column: 1/-1 !important;
          grid-row: auto !important;
        }
      }
      div.title {
        font-size: var(--panel-title-font-size);
        font-weight: var(--panel-title-weight);
        margin-bottom: var(--panel-margin-bottom);
        color: var(--label-fgcolor);
      }
      div#outlet {
        height: 100%;
      }
    `
  ]
  async loadAndExecuteModule() {
    // Check if the user is signed in and the module is not already loaded
    if (!this.user.signedIn || this.prot !== undefined) return

    try {
      // Generate the signed URL for the .js file
      const { data: jsData } = await supabase.storage
        .from('auth-only')
        .createSignedUrl(`private/conf-${CONFIG.shortName}.js`, 60)
      if (jsData) {
        const { signedUrl: jsSignedUrl } = jsData
        // Fetch the .js file and add the sourceMappingURL
        const jsResponse = await fetch(jsSignedUrl)
        const jsText = await jsResponse.text()
        let modifiedJsText = jsText
        // Check if the user is an authenticated developer
        if (this.isUserDeveloper(this.user)) {
          // Generate the signed URL for the .js.map file
          const { data: mapData } = await supabase.storage
            .from('auth-only')
            .createSignedUrl(`private/conf-${CONFIG.shortName}.js.map`, 60)
          if (mapData) {
            const sourceMappingURL = `//# sourceMappingURL=${mapData.signedUrl}`
            modifiedJsText += `\n${sourceMappingURL}`
          }
        }
        // Blob and object URL to execute the modified script
        const blob = new Blob([modifiedJsText], { type: 'text/javascript' })
        const moduleUrl = URL.createObjectURL(blob)
        this.prot = await import(moduleUrl)
        // Perform additional setup after module is loaded
        this.afterModuleLoad()
        return this.prot
      }
    } catch (error) {
      console.error('Error loading module:', error)
      throw error
    }
    return 
  }

  isUserDeveloper(user: User): boolean {
    // Implement logic to determine if the user is a developer
    return user.session?.user.email === 'kjh@entel.com' // Example check
  }

  afterModuleLoad() {
    // Logic after the module is loaded, e.g., updating routes
    // ...
  }

  private protTask = new Task(this, {
    task: async ([], { }) => {
      return await this.loadAndExecuteModule()
      /*
      if (!this.user.signedIn || this.prot !== undefined)
        return undefined // only load prot if signed in and not already loaded
      const { data, error } =
        await supabase.storage
            .from('auth-only')
            .createSignedUrl(`private/conf-${CONFIG.shortName}.js`, 60)
      if (data) {
        const { signedUrl } = data
        // console.log(signedUrl)
        import(signedUrl).then((module) => {
          this.prot = module
          // console.log(`protTask: before: routes=`, router?.getRoutes())
          if (globalThis.routes) {
            router?.setRoutes([
              ...this.routes.slice(0, -1),
              ...globalThis.routes,
              this.catchAllRoute
            ], false)
            // console.log(`protTask: after: routes=`, router?.getRoutes())
            Router.go(window.location.pathname)
          }
          // console.log(`  module=`, module)
          this.requestUpdate()
          return this.prot
        }).catch((error) => {
          // Handle any errors that occurred while importing the module
          console.error(`Error 1: `, error)
          throw error
        })
      } else {
        console.error(`Error 2: `, error)
        throw error
      }
    */
    },
    args: () => []
  })

  async connectedCallback() {
    super.connectedCallback()
    const { data, error } = await supabase.auth.getSession()
    if (error)
      console.error('ECSettings.connectedCallback: error=', error)
    else if (data === null || data === undefined)
      console.error(`ECSettings.connectedCallback: data is ${data}`)
    else if (data.session === undefined) {
      this.user.session = null
      this.user.signedIn = false
    } else {
      this.user.session = data.session
      this.user.signedIn = true
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        // console.log(event)
        switch (event) {
          case 'INITIAL_SESSION':
            this.user = { 
              signedIn: session !== null,
              session: session,
              avatarURL: session?.user?.user_metadata?.avatar_url || ''
            }
            this.protTask.run()
            break
          case 'SIGNED_IN':
            this.user = {
              signedIn: true,
              session: session,
              avatarURL: session?.user?.user_metadata?.avatar_url || ''
            }
            this.protTask.run()
            break
          case 'USER_UPDATED':
            this.user = {
              ...this.user,
              avatarURL: session?.user?.user_metadata?.avatar_url || ''
            }
            break
          case 'SIGNED_OUT':
            this.user = {
              signedIn: false,
              session: null,
              avatarURL: ''
            }
            break
        }
      })
    this.onAuthStateChangeSubscription = subscription
  }
  disconnectedCallback(): void {
    if (this.onAuthStateChangeSubscription !== undefined)
      this.onAuthStateChangeSubscription.unsubscribe()
    super.disconnectedCallback()
  }

  async firstUpdated(changedProperties: PropertyValues)  {
    super.firstUpdated(changedProperties)
    // console.log(`ECApp.firstUpdated()`)
    const outlet = this.shadowRoot?.querySelector('#outlet')
    if (outlet) {
      router = new Router(outlet)
      router.setRoutes(this.routes, this.user.signedIn) // skip render if signed in because will want to render after updating routes

    } else {
      console.log('Could not find outlet element')
    }
  }

  render() {
    return html`
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet"/>
      <div class="page">
        <ec-spaceport class="page-header">
          <ec-header title="${CONFIG.serviceName}"></ec-header>
        </ec-spaceport>
        <ec-spaceport >
          <!-- ${this.protTask.render({
            initial: () => html`<p>Waiting to start task</p>`,
            pending: () => html`<p>Running task...</p>`,
            complete: (value: unknown) => html`
              <p>The task completed with: </p>
              <pre>${value}</pre>`,
            error: (error: unknown) => html`
              <p>Oops, something went wrong:</p>
              <pre>${error}</pre>`,
          })} -->
          <div id="outlet"></div>
        </ec-spaceport>
        <ec-spaceport class="page-footer">
          <ec-footer></ec-footer>
        </ec-spaceport>
      </div>
    `
  }
}
export var router: Router | undefined = undefined
export interface ProtectedModule {
  signedOutHomeComponent: TemplateResult
  signedInHomeComponent: TemplateResult
  routes: Route[]
}

export interface User {
  signedIn: boolean
  session: Session | null
  avatarURL: string 
}
